/**
 * Captures the browser's install prompt (`beforeinstallprompt`) at module
 * scope — i.e. as soon as the bundle evaluates, *before* React renders.
 *
 * Registering this from a component effect is too late: Chrome can fire the
 * event while the first paint is still settling, and a missed event means the
 * Install button never appears for that session.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: BeforeInstallPromptEvent | null = null

const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((listener) => listener())
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep the default browser mini-infobar quiet — the page renders its own
    // install UI, so defer the prompt until the user taps the button.
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify()
  })

  window.addEventListener('appinstalled', () => {
    deferred = null
    notify()
  })
}

/** The pending install prompt, if the browser offered one. */
export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferred
}

/**
 * Shows the install dialog. The prompt object is single-use, so it is cleared
 * from the module the moment it is handed to the browser — otherwise the page
 * would keep offering a button that can no longer do anything.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  const event = deferred
  if (!event) return null
  deferred = null
  notify()
  try {
    await event.prompt()
    const choice = await event.userChoice
    return choice.outcome
  } catch {
    return null
  }
}

/** Subscribe to install-prompt changes. Returns an unsubscribe function. */
export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** True when the app is running from a home-screen / taskbar shortcut. */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}
